import { useEffect, useRef, useState } from 'react';
import './SteamLayer.css';

interface Props {
  intensity?: 'subtle' | 'strong';
}

function safeMatch(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return window.matchMedia(query).matches; } catch { return false; }
}

export default function SteamLayer({ intensity = 'subtle' }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  // Coarse-pointer (phones) clamps "strong" to "subtle": filter:blur(8-9px)
  // on 5 puffs in ProductModal stacks expensively on iOS Safari, especially
  // alongside the modal's own backdrop and order form repaints.
  const [coarse] = useState(() => safeMatch('(pointer: coarse)'));
  const effective = coarse && intensity === 'strong' ? 'subtle' : intensity;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => setVisible(entries[0]?.isIntersecting ?? false),
      { rootMargin: '120px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={rootRef} aria-hidden="true" className={`steam-layer steam-layer--${effective}`}>
      {visible && (
        <>
          <span className="steam-puff steam-puff--1" />
          <span className="steam-puff steam-puff--2" />
          <span className="steam-puff steam-puff--3" />
          {effective === 'strong' && (
            <>
              <span className="steam-puff steam-puff--4" />
              <span className="steam-puff steam-puff--5" />
            </>
          )}
        </>
      )}
    </div>
  );
}
